import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Chat,
  People,
  Settings,
  Assessment,
  Label,
  Group,
  QuestionAnswer,
  Schedule,
  Notifications,
  ExitToApp,
  Person,
  Campaign,
  Hub,
  ManageAccounts,
  ContactPage,
  Inbox,
  Extension,
  Autorenew,
  LibraryBooks,
  Security,  // Added: 2024-11-12 - User Access Management Module Phase 4.5
  ThumbsUpDown,  // Added: 2025-11-18 - CSAT Analytics
  WhatsApp  // Added: 2026-01-19 - WhatsApp Templates
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { useKeyboardShortcuts } from '../../contexts/KeyboardShortcutsContext';
import CommandBar from '../CommandBar/CommandBar';
import ShortcutsHelpModal from '../KeyboardShortcuts/ShortcutsHelpModal';
import NotificationsDropdown from '../Notifications/NotificationsDropdown';
import { conversationsAPI } from '../../services/api';
import { usePermission, useAnyPermission } from '../../hooks/usePermission'; // 14/11/2025 - User Access Management Module Phase 5.2.2

const drawerWidth = 260;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [conversationCount, setConversationCount] = useState<number>(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { isCommandBarOpen, isHelpModalOpen, closeCommandBar, closeHelpModal } = useKeyboardShortcuts();

  // Fetch active conversation count
  useEffect(() => {
    const fetchConversationCount = async () => {
      try {
        // Fetch only active conversations (not resolved)
        const response = await conversationsAPI.getConversations({
          status: 'open' as any
        });
        setConversationCount(response.total || response.conversations?.length || 0);
      } catch (error) {
        console.error('Failed to fetch conversation count:', error);
      }
    };

    fetchConversationCount();

    // Poll every 30 seconds to keep count updated
    const interval = setInterval(fetchConversationCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleProfileMenuClose();
  };

  // 14/11/2025 - User Access Management Module Phase 5.2.2
  // Define all menu items with required permissions
  const allMenuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Conversations', icon: <Chat />, path: '/dashboard/conversations', badge: conversationCount, requiredPermissions: ['conversations:view_all', 'conversations:view_team', 'conversations:view_assigned'] },
    { text: 'Contacts', icon: <People />, path: '/dashboard/contacts', requiredPermissions: ['contacts:view'] },
    { text: 'Inboxes', icon: <Inbox />, path: '/dashboard/inboxes', requiredPermissions: ['inboxes:view'] },
    { text: 'Integrations', icon: <Extension />, path: '/dashboard/integrations', requiredPermissions: ['integrations:view'] },
    { text: 'Reports', icon: <Assessment />, path: '/dashboard/reports', requiredPermissions: ['reports:view'] },
    { text: 'Automation', icon: <Autorenew />, path: '/dashboard/automation', requiredPermissions: ['automation:view'] },
    { text: 'Labels', icon: <Label />, path: '/dashboard/labels', requiredPermissions: ['labels:view'] },
    { text: 'Teams', icon: <Group />, path: '/dashboard/teams', requiredPermissions: ['teams:view'] },
    { text: 'Agents', icon: <Person />, path: '/dashboard/agents', requiredPermissions: ['agents:view'] },
    { text: 'Roles & Permissions', icon: <Security />, path: '/dashboard/roles', requiredPermissions: ['roles:view'] },
    { text: 'Canned Responses', icon: <QuestionAnswer />, path: '/dashboard/canned-responses', requiredPermissions: ['canned_responses:view'] },
    { text: 'Campaigns', icon: <Campaign />, path: '/dashboard/campaigns', requiredPermissions: ['campaigns:view'] },
    { text: 'WhatsApp Templates', icon: <WhatsApp />, path: '/dashboard/whatsapp-templates', requiredPermissions: ['campaigns:view'] },  // Added: 2026-01-19
    { text: 'Pre-Chat Forms', icon: <ContactPage />, path: '/dashboard/pre-chat-forms', requiredPermissions: ['pre_chat_forms:view'] },
    { text: 'Agent Capacity', icon: <ManageAccounts />, path: '/dashboard/agent-capacity' },
    { text: 'Slack Integration', icon: <Hub />, path: '/dashboard/slack-integration', requiredPermissions: ['slack_integration:view'] },
    { text: 'Business Hours', icon: <Schedule />, path: '/dashboard/business-hours', requiredPermissions: ['business_hours:view'] },
    { text: 'Knowledge Base', icon: <LibraryBooks />, path: '/dashboard/knowledge-base', requiredPermissions: ['knowledge_base:view'] },
    { text: 'CSAT Analytics', icon: <ThumbsUpDown />, path: '/dashboard/csat-analytics' },  // Added: 2025-11-18 - Widget CSAT
    { text: 'Settings', icon: <Settings />, path: '/dashboard/settings', requiredPermissions: ['settings:view'] },
  ];

  // 14/11/2025 - User Access Management Module Phase 5.2.2
  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    // Super admins see everything
    if (user?.role?.toUpperCase() === 'SUPER_ADMIN') {
      return allMenuItems;
    }

    // Filter based on permissions
    return allMenuItems.filter(item => {
      // Items without required permissions are always shown (like Dashboard, Agent Capacity)
      if (!item.requiredPermissions) {
        return true;
      }

      // Check if user has ANY of the required permissions (OR logic)
      return item.requiredPermissions.some(perm =>
        user?.permissions?.includes(perm)
      );
    });
  }, [user?.permissions, user?.role, conversationCount]);

  const isSelected = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
          <img
            src="/logo/rr_logo.png"
            alt="Converse"
            style={{ width: '200px', height: '40px', objectFit: 'contain' }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isSelected(item.path)}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                  color: '#1f93ff',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ 
                color: isSelected(item.path) ? '#1f93ff' : 'inherit',
                minWidth: 40 
              }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'black',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {location.pathname === '/dashboard' ? 'Dashboard' :
             location.pathname.includes('conversations') ? 'Conversations' :
             location.pathname.includes('contacts') ? 'Contacts' :
             location.pathname.includes('inboxes') ? 'Inboxes' :
             location.pathname.includes('integrations') ? 'Integrations' :
             location.pathname.includes('reports') ? 'Reports' :
             location.pathname.includes('automation') ? 'Automation' :
             location.pathname.includes('labels') ? 'Labels' :
             location.pathname.includes('teams') ? 'Teams' :
             location.pathname.includes('agents') ? 'Agents' :
             location.pathname.includes('canned-responses') ? 'Canned Responses' :
             location.pathname.includes('campaigns') ? 'Campaigns' :
             location.pathname.includes('pre-chat-forms') ? 'Pre-Chat Forms' :
             location.pathname.includes('agent-capacity') ? 'Agent Capacity' :
             location.pathname.includes('slack-integration') ? 'Slack Integration' :
             location.pathname.includes('business-hours') ? 'Business Hours' :
             location.pathname.includes('settings') ? 'Settings' : 'Dashboard'}
          </Typography>
          
          <NotificationsDropdown />
          
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar
              src={user?.avatar_url || undefined}
              sx={{ bgcolor: '#1f93ff' }}
            >
              {user?.first_name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => navigate('/dashboard/settings')}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid #e0e0e0',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
      
      {/* Command Bar */}
      <CommandBar open={isCommandBarOpen} onClose={closeCommandBar} />
      
      {/* Keyboard Shortcuts Help Modal */}
      <ShortcutsHelpModal open={isHelpModalOpen} onClose={closeHelpModal} />
    </Box>
  );
};

export default DashboardLayout;